import React from "react";
import { Crown, BarChart3, Trophy, Award } from "lucide-react";
import { Link } from "react-router-dom";
import MagicalCard from "./MagicalCard";
import Avatar from "./Avatar";
import Sparkles from "./Sparkles";
import { useLang } from "../i18n";
import { useConfig } from "../context/ConfigContext";
import { wsLabel } from "../utils";

export default function ProfileCard({ student, totalPoints, level, rank, badgeCount, nearThreshold = 20 }) {
  const { t, pick, lang } = useLang();
  const { config } = useConfig();
  if (!student || !level) return null;
  const cur = level.current;
  const next = level.next;
  const near = next && level.remaining > 0 && level.remaining <= nearThreshold;
  const pointsLabel = wsLabel(config?.ui_labels, "points", lang, t("dash.points_label"));
  return (
    <MagicalCard title={t("dash.student_profile")} variant="ribbon-pink" testId="profile-card" hover={false}>
      <Sparkles />
      <div className="flex flex-col items-center text-center relative">
        <div className="relative">
          <Avatar src={student.avatar} name={pick(student, "name")} size="xl" crown />
          <span className="absolute -top-3 start-1/2 -translate-x-1/2 rtl:translate-x-1/2">
            <Crown className="w-8 h-8 text-[#FFC83D] animate-floaty" fill="currentColor" style={{ filter: "drop-shadow(0 0 8px rgba(255,200,61,0.7))" }} />
          </span>
        </div>
        <h3 data-testid="profile-name" className="mt-3 text-2xl font-black">{pick(student, "name")}</h3>
        <p className="text-xs text-[#A5A0D6] font-bold mt-0.5">{lang === "ar" ? (student.class_name_ar || student.class_name_en) : (student.class_name_en || student.class_name_ar) || ""}</p>

        <div className="mt-4 w-full rounded-2xl bg-[#0D0B38]/60 border border-white/10 p-4">
          <p className="text-xs text-[#A5A0D6] font-bold">{t("dash.current_level")}</p>
          <div className="flex items-center justify-center gap-2 mt-1">
            <span data-testid="profile-level" className="text-xl font-black" style={{ color: cur?.color || "#FFC83D" }}>
              {cur ? pick(cur, "name") : "—"}
            </span>
            <span className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${cur?.color}33`, boxShadow: `0 0 10px ${cur?.color}55` }}>
              <Award className="w-4 h-4" style={{ color: cur?.color }} />
            </span>
          </div>
          <p className="text-xs text-[#A5A0D6] font-bold mt-3">{pointsLabel}</p>
          <p data-testid="profile-points" className="text-3xl font-black text-[#48D276]" style={{ textShadow: "0 0 18px rgba(72,210,118,0.45)" }}>
            {totalPoints}{next ? <span className="text-lg text-[#A5A0D6]"> / {next.min_points}</span> : null}
          </p>
          {next ? (
            <>
              <div className={`progress-track h-4 mt-2 ${near ? "animate-pulse-glow" : ""}`}>
                <div data-testid="profile-progress" className="progress-fill h-full" style={{ width: `${level.progress}%` }} />
              </div>
              <p data-testid="profile-remaining" className={`text-xs font-extrabold mt-2 ${near ? "text-[#FFC83D]" : "text-[#D8D4F2]"}`}>
                {t("dash.remaining_to_level", { n: level.remaining, level: pick(next, "name") })}
              </p>
            </>
          ) : (
            <p className="text-sm font-extrabold text-[#FFC83D] mt-2">{t("dash.top_level")}</p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2 w-full mt-3">
          <div className="rounded-xl bg-[#0D0B38]/60 border border-white/10 py-2.5">
            <p className="flex items-center justify-center gap-1 text-lg font-black text-[#27C9EE]"><Trophy className="w-4 h-4" />{rank ?? "—"}</p>
            <p className="text-[10px] text-[#A5A0D6] font-bold">{t("dash.rank")}</p>
          </div>
          <div className="rounded-xl bg-[#0D0B38]/60 border border-white/10 py-2.5">
            <p className="flex items-center justify-center gap-1 text-lg font-black text-[#FF63B5]"><Award className="w-4 h-4" />{badgeCount}</p>
            <p className="text-[10px] text-[#A5A0D6] font-bold">{t("dash.badges_count")}</p>
          </div>
        </div>

        <Link to="/history" data-testid="points-history-link"
          className="mt-4 w-full btn-magic btn-ghost text-sm flex items-center justify-center gap-2">
          <BarChart3 className="w-4 h-4 text-[#27C9EE]" /> {t("dash.points_history")}
        </Link>
      </div>
    </MagicalCard>
  );
}
